// Карта объекта: поставить точку и обвести территорию мышью.
//
// ## Зачем карта, если есть поле «вставьте координаты»
//
// Поле принимает то, что человек скопировал, и этого достаточно, чтобы точка попала в базу.
// Но проверить её этим полем нельзя: 61,25 и 73,39 выглядят одинаково правдоподобно в любом
// порядке, а рамка правдоподобия на сервере ловит не все перевёртыши (Пермь наоборот —
// нормальная точка). Карта закрывает ровно это: человек видит свой объект или чистое поле.
//
// Территорию же руками не задать вообще никак. Круг вокруг точки годится посту в здании и не
// годится базе с забором буквой Г: любой круг вокруг центра либо режет половину площадки, либо
// накрывает соседнюю.
//
// ## Почему Leaflet и OpenStreetMap, а не «Яндекс Карты»
//
// Встроенная карта «Яндекса» требует ключа, а значит регистрации, оплаты по счётчику и
// доступности зарубежной службы с сервера в России. Leaflet — библиотека без ключей, плитки
// OpenStreetMap бесплатны. Объектов у заказчика шестьдесят четыре, и размечают их однажды:
// нагрузка на плитки здесь исчезающая.
//
// ⚠️ **Карта обязана деградировать молча.** Плитки не загрузились (нет сети, заблокировали
// адрес) — человек всё равно видит поле «вставьте координаты» и работает как раньше. Экран,
// который без карты перестаёт заводить объекты, был бы хуже, чем экран без карты.
//
// ## Что здесь важно не сломать
//
// Координаты живут СТРОКАМИ. Leaflet отдаёт числа с плавающей точкой, и обратно в строку они
// переводятся с шестью знаками — ровно столько хранит колонка. Без этого широта 61.254012
// после круга «карта → строка → карта» превращалась бы в 61.25401199999999.

import { useEffect, useRef, useState } from 'react';

import { RiFocus3Line, RiPencilRuler2Line, RiDeleteBinLine } from '@remixicon/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as Label from '@/shared/ui/label';

/** Точка так, как она ездит по сети и лежит в базе: строками. */
export interface ТочкаКарты {
  lat: string;
  lon: string;
}

/**
 * Цвет разметки берётся ИЗ ТЕМЫ, а не вписывается числом.
 *
 * Leaflet рисует не в вёрстке, а в холсте, и принимает цвет строкой — переменную темы туда не
 * подставить. Значит её надо прочитать: тогда тёмная тема окрашивает разметку вместе со всем
 * остальным, а на светлой ничего не меняется. Вписанный шестнадцатеричный цвет остался бы синим
 * на чёрном экране ночью на объекте — ровно тот класс, который сторож оформления и ловит.
 *
 * Запасное значение нужно ровно на один случай: тему ещё не применили (первый кадр, прогон
 * проверок вне браузера). Тогда лучше видимая линия неточного оттенка, чем невидимая.
 */
function цветРазметки(): string {
  if (typeof window === 'undefined') return 'rgb(51 92 255)';
  const тема = getComputedStyle(document.documentElement).getPropertyValue('--primary-base');
  return тема.trim() === '' ? 'rgb(51 92 255)' : `hsl(${тема})`;
}

/** Шесть знаков — ровно столько хранит колонка, то есть около десяти сантиметров. */
function встроку(значение: number): string {
  return значение.toFixed(6);
}

function вчисло(значение: string | null): number | null {
  if (значение === null || значение === '') return null;
  const число = Number(значение);
  return Number.isFinite(число) ? число : null;
}

/**
 * Куда смотреть, когда точки ещё нет.
 *
 * Сургут: там офис заказчика и большинство его объектов. Пустая карта мира заставляет человека
 * сначала найти свою страну, а это три-четыре движения ни за чем.
 */
const СУРГУТ: [number, number] = [61.254, 73.396];

export interface КартаОбъектаProps {
  точка: ТочкаКарты | null;
  область: ТочкаКарты[] | null;
  радиусМетров: number;
  наТочку: (точка: ТочкаКарты) => void;
  наОбласть: (область: ТочкаКарты[] | null) => void;
  /** Только смотреть: карточка объекта показывает то же самое, но менять там нечего. */
  толькоЧтение?: boolean;
}

export function КартаОбъекта({
  точка,
  область,
  радиусМетров,
  наТочку,
  наОбласть,
  толькоЧтение = false,
}: КартаОбъектаProps) {
  const контейнер = useRef<HTMLDivElement | null>(null);
  const карта = useRef<L.Map | null>(null);
  const слой = useRef<L.LayerGroup | null>(null);
  const [обводим, setОбводим] = useState(false);
  const [черновик, setЧерновик] = useState<ТочкаКарты[]>([]);
  const [плиткиМолчат, setПлиткиМолчат] = useState(false);

  // Живые значения и живые обработчики в ссылке.
  //
  // Причина не в стиле. Нажатие по карте вешается ОДИН раз на всю её жизнь: пересоздавать
  // карту на каждое изменение значит терять масштаб и положение, которые человек только что
  // настроил. Обработчик, замкнувший состояние первого кадра, навсегда запомнил бы «обводим =
  // ложь», и обвести территорию было бы нельзя ни разу.
  //
  // Обработчик «метку перетащили» живёт в перерисовке, и там та же ловушка с другой стороны:
  // положи `наТочку` в список зависимостей — перерисовка пойдёт на каждый кадр родителя,
  // потому что стрелочная функция каждый раз новая, и слои карты будут стираться под рукой.
  const состояние = useRef({ обводим, черновик, толькоЧтение, наТочку });
  состояние.current = { обводим, черновик, толькоЧтение, наТочку };

  useEffect(() => {
    if (контейнер.current === null || карта.current !== null) return;
    const м = L.map(контейнер.current, { attributionControl: true }).setView(СУРГУТ, 13);
    // Подпись самой библиотеки («Leaflet» с флажком) — не обязательная атрибуция, а ссылка на
    // её сайт, где вместо описания политическое обращение. Рабочей программе охранного
    // предприятия вести туда незачем. Обязательная подпись данных карты — «© OpenStreetMap»
    // у слоя плиток — остаётся.
    м.attributionControl.setPrefix(false);
    const плитки = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    });
    плитки.on('tileerror', () => setПлиткиМолчат(true));
    плитки.addTo(м);
    слой.current = L.layerGroup().addTo(м);

    м.on('click', (событие: L.LeafletMouseEvent) => {
      const текущее = состояние.current;
      if (текущее.толькоЧтение) return;
      const новая = {
        lat: встроку(событие.latlng.lat),
        lon: встроку(событие.latlng.lng),
      };
      if (текущее.обводим) {
        setЧерновик([...текущее.черновик, новая]);
        return;
      }
      текущее.наТочку(новая);
    });
    карта.current = м;

    return () => {
      м.remove();
      карта.current = null;
      слой.current = null;
    };
    // Пустой список зависимостей осознан: карта создаётся один раз и живёт до ухода экрана.
    // Всё живое приходит в неё через `состояние`, поэтому сторож зависимостей здесь молчит
    // сам — подавлять его не пришлось.
  }, []);

  // Перерисовка того, что на карте. Отдельно от создания: сюда приходит каждое изменение.
  useEffect(() => {
    const м = карта.current;
    const с = слой.current;
    if (м === null || с === null) return;
    с.clearLayers();

    const цвет = цветРазметки();
    const показываем = обводим ? черновик : (область ?? []);
    if (показываем.length > 0) {
      const вершины = показываем
        .map((т) => [вчисло(т.lat), вчисло(т.lon)] as [number | null, number | null])
        .filter((п): п is [number, number] => п[0] !== null && п[1] !== null);
      // Пока вершин меньше трёх, это ещё не фигура — рисуем ломаной, иначе Leaflet замкнёт
      // две точки в «область» нулевой площади и человек решит, что уже обвёл.
      if (вершины.length >= 3 && !обводим) {
        L.polygon(вершины, { color: цвет, weight: 2, fillOpacity: 0.12 }).addTo(с);
      } else if (вершины.length > 0) {
        L.polyline(вершины, { color: цвет, weight: 2, dashArray: '6 4' }).addTo(с);
        вершины.forEach((в) => {
          L.circleMarker(в, { radius: 4, color: цвет, fillOpacity: 1 }).addTo(с);
        });
      }
    }

    const ш = вчисло(точка?.lat ?? null);
    const д = вчисло(точка?.lon ?? null);
    if (ш !== null && д !== null) {
      const метка = L.marker([ш, д], { draggable: !толькоЧтение }).addTo(с);
      метка.on('dragend', () => {
        const где = метка.getLatLng();
        состояние.current.наТочку({ lat: встроку(где.lat), lon: встроку(где.lng) });
      });
      // Круг рисуется, только когда фигуры нет: заданная фигура ОТМЕНЯЕТ круг, и показывать
      // оба значило бы обещать зону, которой не существует.
      if ((область ?? []).length < 3 && радиусМетров > 0) {
        L.circle([ш, д], {
          radius: радиусМетров,
          color: цвет,
          weight: 1,
          fillOpacity: 0.08,
        }).addTo(с);
      }
    }
  }, [точка, область, радиусМетров, обводим, черновик, толькоЧтение]);

  function кТочке() {
    const ш = вчисло(точка?.lat ?? null);
    const д = вчисло(точка?.lon ?? null);
    if (карта.current !== null && ш !== null && д !== null) {
      карта.current.setView([ш, д], 16);
    }
  }

  // Точка приехала извне (вставили координаты в поле) — показать её, а не оставить человека
  // смотреть на Сургут, когда объект в Иркутске.
  useEffect(кТочке, [точка?.lat, точка?.lon]);

  return (
    <div className='flex flex-col gap-1.5'>
      <Label.Root>Объект на карте</Label.Root>
      <div
        ref={контейнер}
        // Кнопки масштаба и подпись об авторстве рисует сама библиотека карты, своими
        // размерами. Переопределять их значит лезть в чужую вёрстку ради двух кнопок, которыми
        // на планшете и не пользуются: карту там масштабируют щипком.
        data-цель='плотная'
        className='h-72 w-full overflow-hidden rounded-10 border border-stroke-soft-200'
        // Карта — не картинка: человеку с клавиатуры она бесполезна, и объявлять её
        // изображением значит соврать программе чтения с экрана. Работу без мыши делает
        // соседнее поле «где объект на карте».
        role='application'
        aria-label='Карта объекта: нажатие ставит точку'
      />

      {плиткиМолчат && (
        <Hint.Root>
          Карта не загрузилась: нет сети или адрес карт закрыт. Точку можно вставить полем ниже, всё
          остальное работает как обычно.
        </Hint.Root>
      )}

      {!толькоЧтение && (
        <div className='flex flex-wrap items-center gap-2'>
          {!обводим ? (
            <>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xxsmall'
                onClick={() => {
                  setЧерновик([]);
                  setОбводим(true);
                }}
              >
                <Button.Icon as={RiPencilRuler2Line} />
                {(область ?? []).length >= 3 ? 'Обвести заново' : 'Обвести территорию'}
              </Button.Root>
              {(область ?? []).length >= 3 && (
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='ghost'
                  size='xxsmall'
                  onClick={() => naОбластьПусто(наОбласть)}
                >
                  <Button.Icon as={RiDeleteBinLine} />
                  Убрать область
                </Button.Root>
              )}
            </>
          ) : (
            <>
              <span className='text-paragraph-xs text-text-sub-600'>
                Нажимайте по углам территории. Поставлено точек: {черновик.length}
              </span>
              <Button.Root
                type='button'
                variant='primary'
                mode='filled'
                size='xxsmall'
                disabled={черновик.length < 3}
                onClick={() => {
                  наОбласть(черновик);
                  setОбводим(false);
                }}
              >
                Готово
              </Button.Root>
              <Button.Root
                type='button'
                variant='neutral'
                mode='ghost'
                size='xxsmall'
                onClick={() => {
                  setЧерновик([]);
                  setОбводим(false);
                }}
              >
                Отмена
              </Button.Root>
            </>
          )}
          {точка !== null && (
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xxsmall'
              onClick={кТочке}
            >
              <Button.Icon as={RiFocus3Line} />К объекту
            </Button.Root>
          )}
        </div>
      )}

      <Hint.Root>
        {обводим
          ? 'Нужно не меньше трёх углов. Обведённая территория заменяет круг: внутри неё человек считается на месте, снаружи — нет.'
          : 'Нажмите по карте, чтобы поставить точку объекта, или перетащите метку. Территорию с непростой формой обведите — тогда круг не используется.'}
      </Hint.Root>
    </div>
  );
}

/** Убрать область — это прислать пустоту, а не «не трогать». Отдельной функцией ради читаемости. */
function naОбластьПусто(наОбласть: (область: ТочкаКарты[] | null) => void) {
  наОбласть(null);
}
